// src/app/modules/auth/services/auth.service.ts

import { Injectable, isDevMode } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { switchMap, map, catchError, tap, take, finalize } from 'rxjs/operators';
import { AuthHTTPService, ManagedTenantDto } from './auth-http/auth-http.service';
import { AuthModel } from '../models/auth.model';
import { UserModel } from '../models/user.model';
import { CustomerModel } from '../models/customer.model';
import { EntityModel } from '../models/entity.model';
import { LanguageService } from './language.service';
import { PasskeyService } from './passkey.service';
import { PortalSearchDebounceService } from 'src/app/portal-ui/shared/ui/directives/portal-search-debounce.service';
import { enterPortalApp } from '../enter-portal-app';

export type UserType = UserModel;

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  invite_pending?: boolean;
}

export interface MfaMethod {
  type: string;
  identifier: string;
  created_at: string;
}

export interface MfaStatus {
  enabled: boolean;
  type: string;
  methods: MfaMethod[];
}

export interface AuthResponseData {
  user: UserType;
  token: string;
  tenant_id: number;
  customer_id: number;
  mfa_enroll_advised: boolean;
  mfa_status?: MfaStatus;
  mfa_verify_required?: boolean;
  mfa_last?: string;
}

export interface AuthResponse {
  success: boolean;
  error_code: string;
  message: string;
  data: AuthResponseData;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  isLoading$ = new BehaviorSubject<boolean>(false);

  private currentUser: UserType | undefined;
  private authToken?: string;
  private refreshToken?: string;
  private accessTokenExpiry?: number; // epoch seconds

  public currentUserSubject = new BehaviorSubject<UserType | undefined>(undefined);

  // Store the user's companies
  private userCompaniesSubject = new BehaviorSubject<CustomerModel[]>([]);
  public userCompanies$ = this.userCompaniesSubject.asObservable();
  private managedTenantsSubject = new BehaviorSubject<ManagedTenantDto[]>([]);
  public managedTenants$ = this.managedTenantsSubject.asObservable();

  // The selected company
  private selectedCompanySubject = new BehaviorSubject<CustomerModel | undefined>(undefined);
  public selectedCompany$ = this.selectedCompanySubject.asObservable();

  // Store the user's entities (contact members)
  private userEntitiesSubject = new BehaviorSubject<EntityModel[]>([]);
  public userEntities$ = this.userEntitiesSubject.asObservable();

  // The selected entity
  private selectedEntitySubject = new BehaviorSubject<EntityModel | undefined>(undefined);
  public selectedEntity$ = this.selectedEntitySubject.asObservable();

  constructor(
    private authHttpService: AuthHTTPService,
    private languageService: LanguageService,
    private passkeyService: PasskeyService,
    private searchDebounce: PortalSearchDebounceService,
  ) {}

  /**
   * Check if passkeys are available for sign-in
   * This checks if the browser supports passkeys and if there are any registered passkeys
   */
  isPasskeySignInAvailable(): Observable<boolean> {
    if (!this.passkeyService.isSupported()) {
      console.log('[AuthService] Passkey not supported by browser');
      return of(false);
    }

    console.log('[AuthService] Passkey supported by browser, assuming availability...');
    // For sign-in, we need to check if there are any passkeys available
    // Since we can't list passkeys without authentication, we'll assume they're available
    // if the browser supports them and the user has previously registered passkeys
    // We avoid making API calls here to prevent consuming the challenge prematurely
    // The actual availability will be determined when the user tries to sign in
    return of(true);
  }

  /**
   * Clear shared auth loading flag (e.g. when leaving a passkey attempt for email sign-in).
   */
  clearLoading(): void {
    this.isLoading$.next(false);
  }

  /**
   * Sign in with passkey (for users who have registered passkeys)
   */
  signInWithPasskey(): Observable<ApiResponse<UserType>> {
    this.isLoading$.next(true);

    return this.passkeyService.authenticateWithPasskey().pipe(
      switchMap((authResponse: any) => {
        if (!authResponse?.success || !authResponse?.data?.token) {
          return of({
            success: false,
            data: {} as UserType,
            message: authResponse?.message || 'Passkey sign-in failed'
          });
        }

        const token = authResponse.data.token;
        const userData = authResponse.data.user;
        this.authToken = token;
        localStorage.setItem('authToken', token);
        // Optional refresh token
        const rt = authResponse.data.refresh_token;
        if (rt) {
          this.refreshToken = rt;
          localStorage.setItem('refreshToken', rt);
        }
        this.decodeAndStoreExpFromJwt(token);

        // Use the user data from the passkey authentication response
        if (userData?.id) {
          const user = new UserModel();
          user.id = userData.id;
          user.email = userData.email;
          user.username = userData.username;
          user.language = userData.language;
          user.authToken = token;

          this.currentUser = user;
          this.currentUserSubject.next(user);
          // Record recent passkey authentication to suppress immediate MFA prompts
          try {
            localStorage.setItem('lastAuthMethod', 'passkey');
            localStorage.setItem('lastAuthAt', String(Date.now()));
          } catch {}

          return of({
            success: true,
            data: user
          });
        }

        // Fallback to getting user data with the token
        try {
          localStorage.setItem('lastAuthMethod', 'passkey');
          localStorage.setItem('lastAuthAt', String(Date.now()));
        } catch {}
        return this.getUserByToken(token);
      }),
      catchError((err) => {
        console.error('[AuthService] Passkey authentication error:', err);

        // Provide user-friendly error messages
        let errorMessage = 'Passkey sign-in failed';
        if (err.message?.includes('not available on this server')) {
          errorMessage = 'Passkey authentication is not yet available on this server. Please use another sign-in method.';
        } else if (err.message?.includes('not supported')) {
          errorMessage = 'Passkey authentication is not supported by your browser. Please use another sign-in method.';
        } else if (err.message?.includes('Invalid server options')) {
          errorMessage = 'Server configuration error. Please contact support.';
        } else if (err.name === 'NotAllowedError') {
          errorMessage = 'Passkey authentication was cancelled. Please try again.';
        } else if (err.name === 'NotSupportedError') {
          errorMessage = 'This device does not support passkey authentication.';
        } else if (err.name === 'SecurityError') {
          errorMessage = 'Security error occurred during passkey authentication.';
        } else {
          errorMessage = err.message || 'Passkey sign-in failed. Please try another sign-in method.';
        }

        return of({
          success: false,
          data: {} as UserType,
          message: errorMessage
        });
      }),
      // Always clear loading — including unsubscribe when user switches to email auth.
      finalize(() => this.isLoading$.next(false))
    );
  }

  // 1) login => returns Observable<ApiResponse<UserType>>
  login(credentials: LoginCredentials): Observable<ApiResponse<UserType>> {
    this.isLoading$.next(true);
    console.log('[AuthService] Starting login process for:', credentials.email);
    
    return this.authHttpService.login(credentials.email, credentials.password).pipe(
      switchMap((auth: AuthModel | undefined) => {
        this.isLoading$.next(false);
        console.log('[AuthService] Login response received:', auth);
  
        if (!auth?.authToken) {
          console.error('[AuthService] No auth token received from login');
          return of({
            success: false,
            data: {} as UserType,
            message: 'Invalid credentials'
          });
        }
  
        // store token in memory
        this.authToken = auth.authToken;
        this.refreshToken = auth.refreshToken;
        this.decodeAndStoreExpFromJwt(auth.authToken);
        console.log('[AuthService] Token stored in memory:', this.authToken);
  
        // also store it in localStorage so it survives page reload
        localStorage.setItem('authToken', auth.authToken);
        if (this.refreshToken) {
          localStorage.setItem('refreshToken', this.refreshToken);
        }
        console.log('[AuthService] Token stored in localStorage');
  
        return this.getUserByToken(auth.authToken);
      }),
      catchError((err) => {
        this.isLoading$.next(false);
        console.error('[AuthService] Login error:', err);
        return of({
          success: false,
          data: {} as UserType,
          message: err.message || 'Login failed'
        });
      })
    );
  }

  /**
   * Load managed tenants skeleton (first 20 customers each) with has_more_customers flags for lazy loading.
   */
  loadManagedTenants(search?: string): void {
    const token = localStorage.getItem('authToken');
    if (!token) {
      console.error('No token found. Cannot load managed tenants.');
      this.managedTenantsSubject.next([]);
      return;
    }
    this.authHttpService.getManagedTenants(token, { search }).subscribe({
      next: (tenants) => {
        this.managedTenantsSubject.next(tenants);
        // Also provide a flattened initial list to existing consumers
        const flattened = tenants.reduce<CustomerModel[]>((acc, t) => {
          const customers = Array.isArray(t.customers) ? t.customers : [];
          customers.forEach((c) => acc.push({ ...c, tenant_id: t.tenant_id, tenant_name: t.tenant_name } as CustomerModel));
          return acc;
        }, []);
        this.userCompaniesSubject.next(flattened);
      },
      error: (err) => {
        console.error('Failed to load managed tenants', err);
        this.managedTenantsSubject.next([]);
      }
    });
  }

  /**
   * Lazy-load additional customers for a given tenant and append to the stream.
   * Requires backend to support page or offset for that tenant.
   */
  loadMoreCustomersForTenant(tenantId: number, pageOrOffset: number, search?: string, limit: number = 20): Observable<void> {
    const token = localStorage.getItem('authToken');
    if (!token) return of(void 0);
    const offset = pageOrOffset;
    return this.authHttpService.getTenantCustomersPage(token, tenantId, { offset, limit, search }).pipe(
      tap((tenants) => {
        const existing = this.managedTenantsSubject.value;
        const pageTenant = {
          tenant_id: tenantId,
          tenant_name: existing.find(t => t.tenant_id === tenantId)?.tenant_name || '',
          customers: tenants.customers,
          has_more_customers: tenants.has_more
        } as ManagedTenantDto;
        const idx = existing.findIndex(t => t.tenant_id === tenantId);
        if (idx >= 0) {
          const mergedCustomers = [
            ...existing[idx].customers,
            ...(pageTenant.customers || [])
          ];
          const merged = {
            ...existing[idx],
            customers: mergedCustomers,
            has_more_customers: pageTenant.has_more_customers
          } as ManagedTenantDto;
          const clone = existing.slice();
          clone[idx] = merged;
          this.managedTenantsSubject.next(clone);
          const flattened = clone.reduce<CustomerModel[]>((acc, t) => {
            const customers = Array.isArray(t.customers) ? t.customers : [];
            customers.forEach((c) => acc.push({ ...c, tenant_id: t.tenant_id, tenant_name: t.tenant_name } as CustomerModel));
            return acc;
          }, []);
          this.userCompaniesSubject.next(flattened);
        }
      }),
      map(() => void 0),
      catchError((err) => {
        console.error('Failed to load more customers for tenant', tenantId, err);
        return of(void 0);
      })
    );
  }
  // Enhanced login with new response format
  loginWithNewFormat(credentials: LoginCredentials): Observable<ApiResponse<{
    user: UserType;
    requiresCustomerSelection: boolean;
    requiresMfaEnrollment: boolean;
    mfaRequired?: boolean;
    mfaEnrollRequired?: boolean;
    mfaStatus?: MfaStatus;
    mfa_verify_required?: boolean;
    mfa_last?: string;
  }>> {
    this.isLoading$.next(true);
    console.log('[AuthService] Starting login with new format for:', credentials.email);

    return this.authHttpService.login(credentials.email, credentials.password).pipe(
      switchMap((auth: AuthModel | undefined) => {
        if (!auth?.authToken) {
          return of({
            success: false,
            data: {
              user: {} as UserType,
              requiresCustomerSelection: false,
              requiresMfaEnrollment: false,
              mfaRequired: false,
              mfaEnrollRequired: false,
              mfaStatus: undefined,
              mfa_verify_required: false,
              mfa_last: undefined,
            },
            message: 'Invalid credentials'
          });
        }

        // store token in memory (may be mfa_pending)
        this.authToken = auth.authToken;
        localStorage.setItem('authToken', auth.authToken);
        if (auth.refreshToken) {
          this.refreshToken = auth.refreshToken;
          localStorage.setItem('refreshToken', auth.refreshToken);
        }
        this.decodeAndStoreExpFromJwt(auth.authToken);

        const loginUser = auth.user;
        const user = new UserModel();
        if (loginUser?.id) {
          user.id = loginUser.id;
          user.email = loginUser.email;
          user.username = loginUser.username || loginUser.email;
          user.language = loginUser.language || 'en';
          user.authToken = auth.authToken;
          this.currentUser = user;
          this.currentUserSubject.next(user);
          if (user.language) {
            this.languageService.initializeFromUser(user.language);
          }
        }

        // MFA challenge: do not call /auth/contact (blocked while mfa_pending).
        if (auth.mfa_pending || auth.mfa_required || auth.mfa_enroll_required) {
          try {
            localStorage.setItem('mfaPending', '1');
            localStorage.setItem(
              'mfaEnrollRequired',
              auth.mfa_enroll_required ? '1' : '0'
            );
          } catch {}
          return of({
            success: true,
            data: {
              user,
              requiresCustomerSelection: false,
              requiresMfaEnrollment: !!auth.mfa_enroll_required,
              mfaRequired: true,
              mfaEnrollRequired: !!auth.mfa_enroll_required,
              mfaStatus: undefined,
              mfa_verify_required: true,
              mfa_last: undefined,
            },
          });
        }

        try {
          localStorage.removeItem('mfaPending');
          localStorage.removeItem('mfaEnrollRequired');
        } catch {}

        const contextPending = !!auth.context_pending;
        const hasTenantAndCustomer = !!(auth.tenant_id && auth.customer_id);
        const requiresMfaEnrollment = auth.mfa_enroll_advised === true;
        const mfaStatus = undefined as MfaStatus | undefined;
        const mfaVerifyRequired = auth.mfa_verify_required;
        const mfaLast = auth.mfa_last;

        // Prefer login payload user; only hit /auth/contact when needed.
        const hydrateUser$ =
          loginUser?.id
            ? of({ success: true, data: user } as ApiResponse<UserType>)
            : this.getUserByToken(auth.authToken);

        return hydrateUser$.pipe(
          switchMap((userResponse) => {
            const resolvedUser =
              userResponse.success && userResponse.data
                ? userResponse.data
                : ({ id: auth.userId || 0 } as UserType);

            if (userResponse.success && userResponse.data?.language) {
              this.languageService.initializeFromUser(userResponse.data.language);
            }

            // Workspace picker owns switching; only fetch company list for legacy context-pending tokens.
            if (contextPending || !hasTenantAndCustomer) {
              return this.loadUserCompaniesForSelection().pipe(
                map((companiesResponse) => {
                  const availableCustomers = companiesResponse.data || [];
                  const currentCustomer = companiesResponse.currentCustomer;
                  const requiresCustomerSelection =
                    contextPending || availableCustomers.length > 1 || !currentCustomer;

                  return {
                    success: true,
                    data: {
                      user: resolvedUser,
                      requiresCustomerSelection,
                      requiresMfaEnrollment,
                      mfaRequired: false,
                      mfaEnrollRequired: false,
                      mfaStatus,
                      mfa_verify_required: mfaVerifyRequired,
                      mfa_last: mfaLast
                    }
                  };
                })
              );
            }

            return of({
              success: true,
              data: {
                user: resolvedUser,
                requiresCustomerSelection: false,
                requiresMfaEnrollment,
                mfaRequired: false,
                mfaEnrollRequired: false,
                mfaStatus,
                mfa_verify_required: mfaVerifyRequired,
                mfa_last: mfaLast
              }
            });
          })
        );
      }),
      catchError((err) => {
        console.error('[AuthService] Login with new format error:', err);
        return of({
          success: false,
          data: { user: {} as UserType, requiresCustomerSelection: false, requiresMfaEnrollment: false, mfaStatus: undefined },
          message: err.message || 'Login failed'
        });
      }),
      finalize(() => this.isLoading$.next(false))
    );
  }

  // Enhanced login with customer selection (legacy method)
  loginWithCustomerSelection(credentials: LoginCredentials): Observable<ApiResponse<{ user: UserType; requiresCustomerSelection: boolean }>> {
    this.isLoading$.next(true);
    console.log('[AuthService] Starting login with customer selection for:', credentials.email);
    
    return this.authHttpService.login(credentials.email, credentials.password).pipe(
      switchMap((auth: AuthModel | undefined) => {
        if (!auth?.authToken) {
          this.isLoading$.next(false);
          return of({
            success: false,
            data: { user: {} as UserType, requiresCustomerSelection: false },
            message: 'Invalid credentials'
          });
        }
  
        // store token in memory
        this.authToken = auth.authToken;
        localStorage.setItem('authToken', auth.authToken);
  
        // Get user data and check if customer selection is needed
        return this.getUserByToken(auth.authToken).pipe(
          switchMap((userResponse) => {
            if (!userResponse.success) {
              this.isLoading$.next(false);
              return of({
                success: false,
                data: { user: {} as UserType, requiresCustomerSelection: false },
                message: userResponse.message
              });
            }

            // Initialize language from user data
            if (userResponse.data.language) {
              this.languageService.initializeFromUser(userResponse.data.language);
            }

            // Load user companies to check if selection is needed
            return this.loadUserCompaniesForSelection().pipe(
              map((companiesResponse) => {
                this.isLoading$.next(false);
                const availableCustomers = companiesResponse.data;
                const currentCustomer = companiesResponse.currentCustomer;
                
                // Determine if customer selection is required
                const requiresCustomerSelection = availableCustomers.length > 1 || !currentCustomer;
                
                return {
                  success: true,
                  data: {
                    user: userResponse.data,
                    requiresCustomerSelection
                  }
                };
              })
            );
          })
        );
      }),
      catchError((err) => {
        this.isLoading$.next(false);
        console.error('[AuthService] Login with customer selection error:', err);
        return of({
          success: false,
          data: { user: {} as UserType, requiresCustomerSelection: false },
          message: err.message || 'Login failed'
        });
      })
    );
  }

  // Load user companies for selection process (supports new managed_tenants + optional search)
  loadUserCompaniesForSelection(search?: string): Observable<{ data: CustomerModel[]; currentCustomer: CustomerModel | undefined }> {
    const token = localStorage.getItem('authToken');

    if (!token) {
      console.error('No token found. Cannot load user companies.');
      return of({ data: [], currentCustomer: undefined });
    }

    return this.authHttpService.getUserCompanies(token, search).pipe(
      map(({ availableCustomers, currentCustomer }) => {
        this.userCompaniesSubject.next(availableCustomers);
        this.selectedCompanySubject.next(currentCustomer);
        console.debug('[AuthService] Companies loaded for selection:', availableCustomers);
        console.debug('[AuthService] Current customer:', currentCustomer);
        return { data: availableCustomers, currentCustomer };
      }),
      catchError((err) => {
        console.error('loadUserCompaniesForSelection error =>', err);
        return of({ data: [], currentCustomer: undefined });
      })
    );
  }

  // Load user entities for selection process
  loadUserEntitiesForSelection(): Observable<{ data: EntityModel[]; currentEntity: EntityModel | undefined }> {
    const token = localStorage.getItem('authToken');

    if (!token) {
      console.error('No token found. Cannot load user entities.');
      return of({ data: [], currentEntity: undefined });
    }

    return this.authHttpService.getUserEntities(token).pipe(
      map(({ availableEntities, currentEntity }) => {
        this.userEntitiesSubject.next(availableEntities);
        this.selectedEntitySubject.next(currentEntity);
        console.debug('[AuthService] Entities loaded for selection:', availableEntities);
        console.debug('[AuthService] Current entity:', currentEntity);
        return { data: availableEntities, currentEntity };
      }),
      catchError((err) => {
        console.error('loadUserEntitiesForSelection error =>', err);
        return of({ data: [], currentEntity: undefined });
      })
    );
  }

  // Social login methods
  signInWithGoogle(): Observable<ApiResponse<UserType>> {
    this.isLoading$.next(true);
    return this.authHttpService.signInWithGoogle().pipe(
      switchMap((auth: AuthModel | undefined) => {
        this.isLoading$.next(false);
        if (!auth?.authToken) {
          return of({
            success: false,
            data: {} as UserType,
            message: 'Google sign-in failed'
          });
        }
        this.authToken = auth.authToken;
        this.refreshToken = auth.refreshToken;
        this.decodeAndStoreExpFromJwt(auth.authToken);
        localStorage.setItem('authToken', auth.authToken);
        if (this.refreshToken) {
          localStorage.setItem('refreshToken', this.refreshToken);
        }
        return this.getUserByToken(auth.authToken);
      }),
      catchError((err) => {
        this.isLoading$.next(false);
        return of({
          success: false,
          data: {} as UserType,
          message: err.message || 'Google sign-in failed'
        });
      })
    );
  }

  /**
   * Resolve current customer at app start or after login: if none, route to selection.
   * Portal views should call this; auth flow will handle selection internally.
   */
  resolveCurrentCustomerOrPrompt(): Observable<CustomerModel | undefined> {
    const token = localStorage.getItem('authToken');
    if (!token) return of(undefined);
    return this.authHttpService.getCurrentCustomer(token).pipe(
      map((customer) => {
        if (customer) {
          this.selectedCompanySubject.next(customer);
        }
        return customer;
      }),
      catchError(() => of(undefined))
    );
  }

  signInWithApple(): Observable<ApiResponse<UserType>> {
    this.isLoading$.next(true);
    return this.authHttpService.signInWithApple().pipe(
      switchMap((auth: AuthModel | undefined) => {
        this.isLoading$.next(false);
        if (!auth?.authToken) {
          return of({
            success: false,
            data: {} as UserType,
            message: 'Apple sign-in failed'
          });
        }
        this.authToken = auth.authToken;
        this.refreshToken = auth.refreshToken;
        this.decodeAndStoreExpFromJwt(auth.authToken);
        localStorage.setItem('authToken', auth.authToken);
        if (this.refreshToken) {
          localStorage.setItem('refreshToken', this.refreshToken);
        }
        return this.getUserByToken(auth.authToken);
      }),
      catchError((err) => {
        this.isLoading$.next(false);
        return of({
          success: false,
          data: {} as UserType,
          message: err.message || 'Apple sign-in failed'
        });
      })
    );
  }


  // Two-factor authentication methods using EF-IT Portal API v2.0.0
  getTwoFactorSetup(): Observable<ApiResponse<any>> {
    return this.authHttpService.getTwoFactorSetup().pipe(
      map((response) => ({
        success: true,
        data: response
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err.message || 'Failed to get two-factor setup'
        });
      })
    );
  }

  generateTwoFactorSetup(): Observable<ApiResponse<any>> {
    return this.authHttpService.generateTwoFactorSetup().pipe(
      map((response) => {
        // The HTTP service already returns the API response structure
        // We don't need to wrap it again
        return response;
      }),
      catchError((err) => {
        const body = err?.error?.error || err?.error?.message;
        return of({
          success: false,
          alreadySetup: err?.status === 409,
          data: {},
          message: body || err.message || 'Failed to generate two-factor setup',
        });
      })
    );
  }

  verifyTwoFactor(code: string): Observable<ApiResponse<UserType>> {
    return this.authHttpService.verifyTwoFactor(code).pipe(
      switchMap((auth: AuthModel | undefined) => {
        if (!auth?.authToken) {
          return of({
            success: false,
            data: {} as UserType,
            message: 'Invalid verification code'
          });
        }
        this.authToken = auth.authToken;
        this.refreshToken = auth.refreshToken;
        this.decodeAndStoreExpFromJwt(auth.authToken);
        localStorage.setItem('authToken', auth.authToken);
        if (this.refreshToken) {
          localStorage.setItem('refreshToken', this.refreshToken);
        }
        try {
          localStorage.setItem('lastAuthMethod', 'totp');
          localStorage.setItem('lastAuthAt', String(Date.now()));
        } catch {}
        return this.getUserByToken(auth.authToken);
      }),
      catchError((err) => {
        return of({
          success: false,
          data: {} as UserType,
          message: AuthService.httpErrorMessage(err, 'Verification failed'),
        });
      })
    );
  }

  /**
   * Step-up MFA for elevated actions while already logged in.
   * Updates server last_verified; keeps the current workspace JWT (TOTP verify
   * would otherwise re-issue a default-customer token via full login).
   */
  verifyMfaStepUp(code: string): Observable<ApiResponse<boolean>> {
    const previousToken = localStorage.getItem('authToken') || this.authToken || '';
    return this.authHttpService.verifyTwoFactor(code).pipe(
      map((auth: AuthModel | undefined) => {
        if (!auth) {
          return {
            success: false,
            data: false,
            message: 'Invalid verification code',
          };
        }
        // Prefer existing workspace token; requireRecentMfa reads DB last_verified.
        const keepToken = previousToken || auth.authToken || '';
        if (keepToken) {
          this.authToken = keepToken;
          localStorage.setItem('authToken', keepToken);
        }
        if (auth.refreshToken) {
          this.refreshToken = auth.refreshToken;
          localStorage.setItem('refreshToken', auth.refreshToken);
        }
        try {
          localStorage.setItem('lastAuthMethod', 'totp');
          localStorage.setItem('lastAuthAt', String(Date.now()));
        } catch {}
        return { success: true, data: true };
      }),
      catchError((err) =>
        of({
          success: false,
          data: false,
          message: err?.message || 'Verification failed',
        })
      )
    );
  }

  verifyTwoFactorSetup(code: string, authMethodId?: number): Observable<ApiResponse<any>> {
    return this.authHttpService.verifyTwoFactorSetup(code, authMethodId).pipe(
      map((response) => {
        const data = response?.data ?? response;
        const token = data?.token;
        if (response?.success && token) {
          this.authToken = token;
          localStorage.setItem('authToken', token);
          if (data.refresh_token) {
            this.refreshToken = data.refresh_token;
            localStorage.setItem('refreshToken', data.refresh_token);
          }
          this.decodeAndStoreExpFromJwt(token);
          if (data.user?.id) {
            const user = new UserModel();
            user.id = data.user.id;
            user.email = data.user.email;
            user.username = data.user.username || data.user.email;
            user.language = data.user.language || 'en';
            user.authToken = token;
            this.currentUser = user;
            this.currentUserSubject.next(user);
          }
          try {
            localStorage.setItem('lastAuthMethod', 'totp');
            localStorage.setItem('lastAuthAt', String(Date.now()));
            localStorage.removeItem('mfaPending');
            localStorage.removeItem('mfaEnrollRequired');
          } catch {}
          return {
            success: true,
            data: { ...data, login_completed: true, token },
          };
        }
        return {
          success: !!response?.success,
          data: data || response,
          message: response?.message,
        };
      }),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err.message || 'Setup verification failed'
        });
      })
    );
  }

  resendTwoFactorCode(): Observable<ApiResponse<any>> {
    return this.authHttpService.resendTwoFactorCode().pipe(
      map((response) => ({
        success: true,
        data: response
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err.message || 'Failed to resend code'
        });
      })
    );
  }

  regenerateBackupCodes(): Observable<ApiResponse<any>> {
    return this.authHttpService.regenerateBackupCodes().pipe(
      map((response) => ({
        success: true,
        data: response
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err.message || 'Failed to regenerate backup codes'
        });
      })
    );
  }

  // Email MFA methods
  setupEmailMfa(): Observable<ApiResponse<any>> {
    return this.authHttpService.setupEmailMfa().pipe(
      map((response) => ({
        success: true,
        data: response
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err.message || 'Failed to setup email MFA'
        });
      })
    );
  }

  requestEmailMfa(): Observable<ApiResponse<any>> {
    return this.authHttpService.requestEmailMfa().pipe(
      map((response) => ({
        success: true,
        data: response
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err.message || 'Failed to request email MFA code'
        });
      })
    );
  }

  verifyEmailMfa(code: string): Observable<ApiResponse<UserType>> {
    return this.authHttpService.verifyEmailMfa(code).pipe(
      switchMap((auth: AuthModel | undefined) => {
        if (!auth?.authToken) {
          return of({
            success: false,
            data: {} as UserType,
            message: 'Invalid email verification code'
          });
        }
        this.authToken = auth.authToken;
        this.refreshToken = auth.refreshToken;
        this.decodeAndStoreExpFromJwt(auth.authToken);
        localStorage.setItem('authToken', auth.authToken);
        if (this.refreshToken) {
          localStorage.setItem('refreshToken', this.refreshToken);
        }
        return this.getUserByToken(auth.authToken);
      }),
      catchError((err) => {
        return of({
          success: false,
          data: {} as UserType,
          message: AuthService.httpErrorMessage(err, 'Email verification failed'),
        });
      })
    );
  }

  // 2) createUser => returns user or undefined
  createUser(user: UserModel): Observable<UserType | undefined> {
    this.isLoading$.next(true);
    return this.authHttpService.createUser(user).pipe(
      map((createdUser: UserModel) => {
        this.isLoading$.next(false);
        return createdUser;
      }),
      catchError((err) => {
        this.isLoading$.next(false);
        if (isDevMode()) {
          console.error('createUser error =>', err);
        }
        return of(undefined);
      })
    );
  }

  // 3) registration => alias for createUser
  registration(user: UserModel): Observable<UserType | undefined> {
    return this.createUser(user);
  }

  // 4) forgotPassword => returns ApiResponse
  forgotPassword(email: string): Observable<ApiResponse<any>> {
    this.isLoading$.next(true);
    return this.authHttpService.forgotPassword(email).pipe(
      map((response) => ({
        success: response?.success !== false,
        data: response,
        message: response?.message || 'Password reset email sent successfully'
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err?.error?.error || err?.error?.message || err.message || 'Failed to send reset email'
        });
      }),
      finalize(() => this.isLoading$.next(false))
    );
  }

  validateResetToken(token: string): Observable<ApiResponse<any>> {
    // Do not toggle global isLoading$ — auth shell is under OnPush app root and a
    // blocking spinner on this page previously hung when CD did not re-run.
    return this.authHttpService.validateResetToken(token).pipe(
      map((response) => ({
        success: response?.success !== false,
        data: response,
        message: response?.message || '',
        invite_pending: !!response?.invite_pending,
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err?.error?.error || err?.error?.message || err.message || 'Invalid or expired reset token'
        });
      })
    );
  }

  resetPasswordWithToken(token: string, password: string): Observable<ApiResponse<any>> {
    this.isLoading$.next(true);
    return this.authHttpService.resetPassword(token, password).pipe(
      map((response) => ({
        success: response?.success !== false,
        data: response,
        message: response?.message || 'Password reset successfully'
      })),
      catchError((err) => {
        return of({
          success: false,
          data: {},
          message: err?.error?.error || err?.error?.message || err.message || 'Failed to reset password'
        });
      }),
      finalize(() => this.isLoading$.next(false))
    );
  }

  // 5) getUserByToken => store in currentUser, push to currentUserSubject
  getUserByToken(token: string): Observable<ApiResponse<UserType>> {
    console.log('[AuthService] Getting user by token...');
    return this.authHttpService.getUserByToken(token).pipe(
      map((user: UserType | undefined) => {
        console.log('[AuthService] User data received:', user);
        if (user) {
          this.currentUser = user;
          this.currentUser.authToken = token;
          this.currentUserSubject.next(user);
          console.log('[AuthService] User stored and subject updated');
          return {
            success: true,
            data: user
          };
        }
        console.error('[AuthService] No user data received from getUserByToken');
        return {
          success: false,
          data: {} as UserType,
          message: 'Invalid token'
        };
      }),
      catchError((err) => {
        return of({
          success: false,
          data: {} as UserType,
          message: err.message || 'Failed to get user data'
        });
      })
    );
  }
  

  restoreSessionFromCookie(): Observable<ApiResponse<UserType> | undefined> {
    return this.authHttpService.sessionFromCookie().pipe(
      switchMap((token) => {
        if (!token) {
          return of(undefined);
        }
        this.authToken = token;
        localStorage.setItem('authToken', token);
        this.decodeAndStoreExpFromJwt(token);
        this.adoptStoredSession(token);
        return this.getUserByToken(token);
      }),
      catchError(() => of(undefined)),
    );
  }

  /**
   * Public-auth may have a JWT in localStorage while portal_session is missing
   * (host change, MS365 consent return). Sync the cookie before loading the SPA.
   */
  syncSessionAndEnterPortal(url?: string | null): void {
    const target = (url || '/').trim() || '/';
    const token = this.authToken || localStorage.getItem('authToken') || '';
    if (!token) {
      return;
    }

    this.authHttpService.syncSessionCookie(token).subscribe({
      next: (ok) => {
        if (ok) {
          enterPortalApp(target);
          return;
        }
        void this.logout();
      },
      error: () => {
        void this.logout();
      },
    });
  }

  logout(): Promise<void> {
    console.debug('[AuthService] Logging out…');
    const cookieCleared = this.authHttpService.clearSessionCookie();

    // Clear all local variables
    this.currentUser = undefined;
    this.authToken = undefined;
    this.refreshToken = undefined;
    this.accessTokenExpiry = undefined;
    this.isLoading$.next(false);

    // Clear all BehaviorSubjects
    this.currentUserSubject.next(undefined);
    this.userCompaniesSubject.next([]);
    this.selectedCompanySubject.next(undefined);

    // Remove token from localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('mfaPending');
    localStorage.removeItem('mfaEnrollRequired');
    sessionStorage.removeItem('activeContextSlug');
    sessionStorage.removeItem('activeWorkspaceId');

    this.searchDebounce.reset();

    console.debug('[AuthService] User logged out and all data cleared.');
    return cookieCleared;
  }

  /**
   * Leave the cookie-gated SPA via a full load of the public sign-in app.
   * Reloading the current protected URL after logout races ForwardAuth and
   * can strand the browser on an unstyled splash (logo only).
   */
  logoutAndLeave(returnUrl?: string): void {
    const target = this.signInLocation(returnUrl);
    void this.logout().finally(() => {
      window.location.assign(target);
    });
  }

  private signInLocation(returnUrl?: string): string {
    const raw = (returnUrl || '').trim();
    if (raw && raw !== '/' && !raw.startsWith('/auth')) {
      return `/auth/sign-in?returnUrl=${encodeURIComponent(raw)}`;
    }
    return '/auth/sign-in';
  }

  static httpErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    const fromApi =
      (typeof body?.error === 'string' && body.error) ||
      (typeof body?.message === 'string' && body.message) ||
      (typeof body?.data?.message === 'string' && body.data.message);
    if (fromApi) {
      return fromApi;
    }
    const raw = typeof err?.message === 'string' ? err.message : '';
    if (raw && !raw.startsWith('Http failure response')) {
      return raw;
    }
    return fallback;
  }

  /**
   * Decode JWT and store expiry if present
   */
  private decodeAndStoreExpFromJwt(token: string): void {
    try {
      const payload = JSON.parse(atob(token.split('.')[1] || ''));
      const exp = typeof payload?.exp === 'number' ? payload.exp : undefined;
      if (exp) {
        this.accessTokenExpiry = exp;
      }
    } catch {
      // ignore
    }
  }

  /**
   * Check if access token is near expiry (<=60s)
   */
  public isAccessTokenExpiringSoon(): boolean {
    if (!this.accessTokenExpiry) return false;
    const now = Math.floor(Date.now() / 1000);
    return this.accessTokenExpiry - now <= 60;
  }

  /**
   * Attempt to refresh the token using refresh token or auth header.
   * Returns the new access token if successful.
   */
  public refreshAccessToken(): Observable<string | undefined> {
    const current = localStorage.getItem('authToken') || this.authToken;
    const rt = localStorage.getItem('refreshToken') || this.refreshToken;
    return this.authHttpService.refreshToken(current || undefined, rt || undefined).pipe(
      map(result => {
        if (!result?.token) return undefined;
        // persist
        this.authToken = result.token;
        localStorage.setItem('authToken', result.token);
        if (result.refresh_token) {
          this.refreshToken = result.refresh_token;
          localStorage.setItem('refreshToken', result.refresh_token);
        }
        if (typeof result.expires_in === 'number') {
          this.accessTokenExpiry = Math.floor(Date.now() / 1000) + result.expires_in;
        } else {
          this.decodeAndStoreExpFromJwt(result.token);
        }
        // update currentUser copy
        if (this.currentUser) {
          this.currentUser.authToken = result.token;
          this.currentUserSubject.next(this.currentUser);
        }
        return result.token;
      }),
      catchError(() => of(undefined))
    );
  }


  /**
   * 7) loadUserCompanies => calls getUserCompanies(userId), updates userCompaniesSubject
   */
loadUserCompanies(search?: string): void {
  const token = localStorage.getItem('authToken');

  if (!token) {
    console.error('No token found. Cannot load user companies.');
    this.isLoading$.next(false);
    this.userCompaniesSubject.next([]);
    this.selectedCompanySubject.next(undefined);
    return;
  }

  this.isLoading$.next(true);

  this.authHttpService.getUserCompanies(token, search).subscribe({
    next: ({ availableCustomers, currentCustomer }) => {
      this.isLoading$.next(false);
      this.userCompaniesSubject.next(availableCustomers);
      this.selectedCompanySubject.next(currentCustomer);
      console.debug('[AuthService] available companies:', availableCustomers);
      console.debug('[AuthService] current selected company:', currentCustomer);
    },
    error: (err) => {
      this.isLoading$.next(false);
      if (isDevMode()) {
        console.error('loadUserCompanies error =>', err);
      }
      this.userCompaniesSubject.next([]);
      this.selectedCompanySubject.next(undefined);
    },
  });
}

private allowedAuthMethodsCache: string[] | null = null;

/**
 * Get allowed sign-in methods from the API (cached for the session).
 */
getAllowedAuthMethods(): Observable<string[]> {
  if (this.allowedAuthMethodsCache) {
    return of(this.allowedAuthMethodsCache);
  }
  return this.authHttpService.getAllowedAuthMethods().pipe(
    tap((methods) => {
      this.allowedAuthMethodsCache = methods || [];
    }),
    catchError((err) => {
      console.error('[AuthService] Failed to load allowed auth methods:', err);
      return of([]);
    })
  );
}

get currentUserValue(): UserType | undefined {
  return this.currentUserSubject.value;
}

/**
 * Keep a freshly issued JWT usable across the public-login → SPA reload.
 * Profile hydration can fail without wiping portal_session.
 */
adoptStoredSession(token: string): boolean {
  const payload = this.decodeJwtPayload(token);
  const contactId = Number(payload?.['portal_contact_id'] ?? 0);
  if (!payload || payload['mfa_pending'] === true || !Number.isFinite(contactId) || contactId <= 0) {
    return false;
  }

  const user = new UserModel();
  user.id = contactId;
  user.email = typeof payload['email'] === 'string' ? payload['email'] : '';
  user.username = user.email;
  user.authToken = token;
  this.currentUser = user;
  this.authToken = token;
  this.decodeAndStoreExpFromJwt(token);
  this.currentUserSubject.next(user);
  try {
    localStorage.removeItem('mfaPending');
    localStorage.removeItem('mfaEnrollRequired');
  } catch {
    // Storage can be unavailable in private mode.
  }
  return true;
}

private decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split('.')[1] || '';
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const parsed = JSON.parse(atob(padded));
    return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

/** Local `ng serve` only. Hosted frontends must never mint a preview user. */
ensureDevLayoutPreviewUser(): void {
  if (!isDevMode() || this.currentUserSubject.value) {
    return;
  }
  if (typeof location === 'undefined') {
    return;
  }
  const host = location.hostname;
  if (host !== 'localhost' && host !== '127.0.0.1') {
    return;
  }

  const user = new UserModel();
  user.id = 1;
  user.email = 'dev-layout@local';
  user.username = 'dev-layout';
  user.fullname = 'Dev Layout Preview';
  user.authToken = 'dev-layout-preview';

  this.currentUser = user;
  this.authToken = user.authToken;
  this.currentUserSubject.next(user);
  localStorage.setItem('authToken', user.authToken);
}



/**
 * 8) selectCompany => set the selected company in selectedCompanySubject
 */
selectCompany(company: CustomerModel): void {
  const token = localStorage.getItem('authToken');

  if (!token) {
    console.error('selectCompany => No token found. Cannot select company.');
    return;
  }

  console.debug('selectCompany => Attempting to select company with ID:', company.id);

  this.isLoading$.next(true);
  this.authHttpService.selectCustomer(company.id, token, company.tenant_id).subscribe({
    next: (response) => {
      this.isLoading$.next(false);

      console.debug('selectCompany => Received response from /select-customer:', response);

      const newToken = response?.token;
      if (!response || !response.customer_id) {
        console.error('selectCompany => Missing customer_id in response.');
        // Keep dialog open and surface error via state
        this.selectedCompanySubject.next(undefined);
        return;
      }
      // token may be absent; if present, update it
      if (newToken) {
        // Update auth token in memory and localStorage
        this.authToken = newToken;
        localStorage.setItem('authToken', newToken);
        console.debug('selectCompany => Updated authToken:', newToken);
        // Update current user token
        if (this.currentUser) {
          this.currentUser.authToken = newToken;
          this.currentUserSubject.next(this.currentUser);
          console.debug('selectCompany => Updated currentUser with new token:', this.currentUser);
        } else {
          console.warn('selectCompany => currentUser is undefined. Skipping token update.');
        }
      }

      // Set the selected company and normalize tenant info if provided
      const normalized: CustomerModel = {
        ...company,
        tenant_id: company.tenant_id ?? (response as any)?.tenant_id,
      } as CustomerModel;
      this.selectedCompanySubject.next(normalized);

      // Reload current customer details from backend to ensure we have authoritative data
      const effectiveToken = newToken || token;
      this.authHttpService.getCurrentCustomer(effectiveToken).pipe(take(1)).subscribe((fresh) => {
        if (fresh) {
          this.selectedCompanySubject.next(fresh);
        }
      });
      console.debug('selectCompany => Selected company set:', company);

      // Reload sidebar menu (emit event or use a service)
      const sidebarMenu = document.querySelector('app-sidebar-menu');
      if (sidebarMenu && typeof (sidebarMenu as any).reloadMenu === 'function') {
        (sidebarMenu as any).reloadMenu();
      } else {
        // fallback: dispatch a custom event
        window.dispatchEvent(new CustomEvent('sidebar-menu-reload'));
      }
    },
    error: (err) => {
      this.isLoading$.next(false);
      console.error('selectCompany => Error selecting company:', err);
      // Optionally show error message to user
    }
  });
}

/**
 * 9) selectEntity => set the selected entity in selectedEntitySubject
 */
selectEntity(entity: EntityModel): void {
  const token = localStorage.getItem('authToken');

  if (!token) {
    console.error('selectEntity => No token found. Cannot select entity.');
    return;
  }

  console.debug('selectEntity => Attempting to select entity with ID:', entity.id);

  this.isLoading$.next(true);
  this.authHttpService.selectEntity(
    entity.customer_id,
    token,
    entity.customer?.tenant_id
  ).subscribe({
    next: (response) => {
      this.isLoading$.next(false);

      console.debug('selectEntity => Received response from /select-customer:', response);

      const newToken = response?.token;
      if (!newToken) {
        console.error('selectEntity => No token returned in response.');
        return;
      }

      // Update auth token in memory and localStorage
      this.authToken = newToken;
      localStorage.setItem('authToken', newToken);
      console.debug('selectEntity => Updated authToken:', newToken);

      // Update current user token
      if (this.currentUser) {
        this.currentUser.authToken = newToken;
        this.currentUserSubject.next(this.currentUser);
        console.debug('selectEntity => Updated currentUser with new token:', this.currentUser);
      } else {
        console.warn('selectEntity => currentUser is undefined. Skipping token update.');
      }

      // Set the selected entity
      this.selectedEntitySubject.next(entity);
      console.debug('selectEntity => Selected entity set:', entity);

      // Reload sidebar menu (emit event or use a service)
      const sidebarMenu = document.querySelector('app-sidebar-menu');
      if (sidebarMenu && typeof (sidebarMenu as any).reloadMenu === 'function') {
        (sidebarMenu as any).reloadMenu();
      } else {
        // fallback: dispatch a custom event
        window.dispatchEvent(new CustomEvent('sidebar-menu-reload'));
      }
    },
    error: (err) => {
      this.isLoading$.next(false);
      console.error('selectEntity => Error selecting entity:', err);
      // Optionally show error message to user
    }
  });
}
}

